import { useState } from 'react';

export default function SchedulingSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    preferredDate: '',
    projectScope: [] as string[]
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleCheckboxChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      projectScope: prev.projectScope.includes(value)
        ? prev.projectScope.filter(item => item !== value)
        : [...prev.projectScope, value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const submitData = new URLSearchParams();
    submitData.append('name', formData.name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('propertyType', formData.propertyType);
    submitData.append('preferredDate', formData.preferredDate);
    submitData.append('projectScope', formData.projectScope.join(', '));

    try {
      const response = await fetch('https://readdy.ai/api/form/d5gfcnbihlvellpk4p3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          preferredDate: '',
          projectScope: []
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      number: '01',
      title: 'Initial Consultation',
      description: 'Discuss your vision, requirements, and budget with our expert team',
      icon: 'ri-chat-3-line'
    },
    {
      number: '02',
      title: 'Site Survey',
      description: 'On-site assessment of your property and infrastructure evaluation',
      icon: 'ri-home-line'
    },
    {
      number: '03',
      title: 'Custom Proposal',
      description: 'Detailed system design with equipment specifications and pricing',
      icon: 'ri-file-list-3-line'
    },
    {
      number: '04',
      title: 'Professional Installation',
      description: 'Expert installation by certified technicians with minimal disruption',
      icon: 'ri-tools-line'
    }
  ];

  return (
    <section id="schedule" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A90E2] mb-4 font-light">
            GET STARTED
          </p>
          <h2 className="text-5xl font-serif text-gray-900 mb-6">
            <strong>Simple Process,</strong><br />
            Exceptional Results
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From initial consultation to final installation, we guide you through every step of your <strong>home automation</strong> journey
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#4A90E2] to-gray-200"></div>
              )}

              <div className="relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#4A90E2]/30 hover:shadow-xl transition-all">
                {/* Number Badge */}
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-[#4A90E2] to-[#357ABD] flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  {step.number}
                </div>

                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#4A90E2]/10 flex items-center justify-center mb-6 mt-4">
                  <i className={`${step.icon} text-2xl text-[#4A90E2]`}></i>
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Card */}
        <div className="bg-gradient-to-br from-[#4A90E2] to-[#357ABD] rounded-2xl p-12 text-center text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          <div className="relative z-10">
            <h3 className="text-3xl font-serif mb-4">
              <strong>Ready to Get Started?</strong>
            </h3>
            <p className="text-base text-white/90 mb-8 max-w-2xl mx-auto">
              Schedule your free consultation today and discover how we can transform your property with intelligent automation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+17862829701"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#4A90E2] text-sm font-medium tracking-wide rounded-full hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Call (786) 282-9701
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white text-sm font-medium tracking-wide rounded-full hover:bg-white hover:text-[#4A90E2] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line"></i>
                Schedule Online
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
